import React, { useState } from 'react';
import { Card, Button, Badge } from './ui';

const PROJECT_ID = process.env.REACT_APP_FB_PROJECT_ID || '(nije postavljen)';

function Copyable({ value }) {
  const [copied, setCopied] = useState(false);
  return (
    <span className="inline-flex items-center gap-2">
      <code className="select-all break-all rounded bg-slate-900 px-2 py-1 text-xs text-white">{value}</code>
      <Button
        className="!px-2 !py-1 !text-xs"
        onClick={() => {
          navigator.clipboard?.writeText(value).then(
            () => { setCopied(true); setTimeout(() => setCopied(false), 2000); },
            () => {}
          );
        }}
      >
        {copied ? 'Kopirano ✓' : 'Kopiraj'}
      </Button>
    </span>
  );
}

/**
 * Shown when Firestore refuses to answer. The overwhelmingly common cause is
 * that the rules were never published, or were published with the placeholder
 * UID still in them — so this spells out both, with the real UID to hand.
 */
export default function Diagnostics({ uid, email, errors }) {
  const codes = [...new Set(Object.values(errors).map((e) => e.code))];
  const denied = codes.includes('permission-denied');
  const notFound = codes.includes('not-found') || codes.includes('failed-precondition');
  const unavailable = codes.includes('unavailable');
  const sections = Object.keys(errors);

  return (
    <Card className="mb-6 border-red-300 bg-red-50 p-4">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <h2 className="text-sm font-bold uppercase tracking-wide text-red-900">
          Ne mogu čitati bazu
        </h2>
        {codes.map((c) => <Badge key={c} tone="red">{c}</Badge>)}
      </div>

      <p className="mb-3 text-sm text-red-800">
        Baza <strong>nije prazna</strong> — Firestore odbija odgovoriti, pa admin ne zna što je
        unutra. Zato su sve brojke nula i zato se ništa ne može ni upisati.
        {sections.length < 6 && ` (Ne radi ${sections.length} od 6 sekcija: ${sections.join(', ')}.)`}
      </p>

      {denied && (
        <div className="mb-3 rounded-md border border-red-200 bg-white p-3">
          <p className="mb-2 text-sm font-semibold text-slate-900">
            Pravila te odbijaju. Dvije su moguće stvari:
          </p>
          <ol className="ml-4 list-decimal space-y-2 text-sm text-slate-700">
            <li>
              <strong>Pravila nisu objavljena.</strong> Ako si bazu stvorio u „production mode",
              default pravilo je <code className="rounded bg-slate-100 px-1">allow read, write: if false</code> —
              zabranjeno je baš sve. Pokreni u terminalu:
              <pre className="mt-1 overflow-x-auto rounded bg-slate-900 p-2 text-xs text-white">firebase deploy --only firestore:rules</pre>
            </li>
            <li>
              <strong>U pravilima stoji krivi UID.</strong> Otvori{' '}
              <code className="rounded bg-slate-100 px-1">firestore.rules</code> i provjeri da je
              tamo upravo ovaj UID, a ne <code className="rounded bg-slate-100 px-1">PASTE_YOUR_ADMIN_UID_HERE</code>:
              <div className="mt-2"><Copyable value={uid} /></div>
            </li>
          </ol>
        </div>
      )}

      {notFound && (
        <div className="mb-3 rounded-md border border-red-200 bg-white p-3 text-sm text-slate-700">
          <strong>Baza možda uopće ne postoji.</strong> U Firebase konzoli otvori{' '}
          <em>Build → Firestore Database</em> i klikni <em>Create database</em>. Provjeri i da je
          projekt isti kao ovaj: <code className="rounded bg-slate-100 px-1">{PROJECT_ID}</code>.
        </div>
      )}

      {unavailable && (
        <div className="mb-3 rounded-md border border-red-200 bg-white p-3 text-sm text-slate-700">
          <strong>Nema veze s Firestoreom.</strong> Provjeri internet, ili blokira li nešto
          (adblock, VPN, firewall) <code className="rounded bg-slate-100 px-1">firestore.googleapis.com</code>.
        </div>
      )}

      <details className="text-xs text-slate-600">
        <summary className="cursor-pointer font-semibold">Detalji</summary>
        <dl className="mt-2 space-y-1">
          <div><dt className="inline font-semibold">Projekt: </dt><dd className="inline">{PROJECT_ID}</dd></div>
          <div><dt className="inline font-semibold">Prijavljen: </dt><dd className="inline">{email}</dd></div>
          <div><dt className="inline font-semibold">UID: </dt><dd className="inline break-all">{uid}</dd></div>
          {Object.entries(errors).map(([section, e]) => (
            <div key={section}>
              <dt className="inline font-semibold">{section}: </dt>
              <dd className="inline">{e.code} — {e.message}</dd>
            </div>
          ))}
        </dl>
      </details>
    </Card>
  );
}
